/** @jsxImportSource @emotion/react */
"use client";

import type { FunctionInterpolation, Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import { getContentId, useCurrentQueueItem, usePlayer } from "@packages/media";
import { useRequestUsersMeSettings } from "@packages/sdk";
import type { ThemeType, WithButtonWrapper } from "@packages/ui";
import {
  colors,
  constantSemanticColors,
  cssMediaQueryMinWidthTablet,
  FullscreenModal,
  IconButton,
  spacing,
  TextOnScreenIcon,
  useTheme,
  zIndices,
} from "@packages/ui";
import type { MouseEventHandler } from "react";
import { useContext, useRef, useState } from "react";

import { fontSizeToFontMultiplier } from "../../../utils/settings";
import { TextSettings } from "../../blocks/groups/TextSettings/TextSettings.emotion";
import { FragmentAsChild } from "../../primitives/FragmentAsChild";
import { PlayerInternalContext } from "../MediaPlayer.emotion";
import { Transcript } from "../TranscriptV2/Transcript.emotion";
import type { TranscriptTheme } from "../TranscriptV2/TranscriptSettingsContext";
import { FollowAudioButton } from "./FollowAudioButton.emotion";

const modalStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.primary,
  padding: 0,
  zIndex: zIndices.transcript,
  [cssMediaQueryMinWidthTablet]: {
    padding: 0,
  },
});
const onColorStyle: Interpolation = {
  color: constantSemanticColors.onColorHighest,
};
const colorModeStyle: FunctionInterpolation<string> = (bgColor) => ({
  backgroundColor: bgColor,
  color: constantSemanticColors.onColorHighest,
});
const hiddenTriggerStyle: Interpolation = {
  background: colors.transparent,
  border: spacing.none,
  height: spacing.none,
  outline: spacing.none,
  overflow: "hidden",
  padding: spacing.none,
  pointerEvents: "none",
  position: "absolute",
  width: spacing.none,
};

// we put a simple z-index here because the layer context of this button is INSIDE the fullscreen-modal,
// so we only need it on top of its parent
const closeButtonStyle: Interpolation = {
  left: spacing.m,
  opacity: 1,
  pointerEvents: "auto",
  position: "fixed",
  top: spacing.m,
  transition: "opacity 250ms ease-out",
  zIndex: zIndices.maskOver,
  [cssMediaQueryMinWidthTablet]: { left: "80px", top: "80px" },
};

export type TextOnScreenProps = WithButtonWrapper<{
  id?: number;
  prayerId?: number;
  iconColor?: string;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}>;

export const TextOnScreen = ({
  id,
  prayerId,
  iconColor,
  Wrapper = FragmentAsChild,
  onClick,
  open,
  onOpenChange,
}: TextOnScreenProps) => {
  const followAudioButtonRef = useRef<HTMLButtonElement>(null);
  const [isManuallyScrolling, setIsManuallyScrolling] = useState(false);
  const { query: settingsQuery } = useRequestUsersMeSettings({});

  const analytics = useAnalytics();
  const t = useTranslations();
  const { popupRef } = useContext(PlayerInternalContext);
  const { tosTrigger } = usePlayer();

  const { currentItem, queue } = useCurrentQueueItem();
  const player = usePlayer();
  const { renderedTheme } = useTheme();

  if (currentItem?.type !== "prayer") {
    return null;
  }

  const handleReport = () => {
    analytics.track({
      event: HallowAnalyticsEvent.TappedReportATranscriptConcern,
      properties: {
        prayer_id: prayerId,
        audio_id: id,
      },
    });
    window.open("https://help.hallow.com");
  };

  if (
    !currentItem ||
    !currentItem.selected_audio.transcript_url ||
    !currentItem.selected_audio.transcript_type
  )
    return (
      <Wrapper asChild>
        <IconButton
          variant={"transparentWhite"}
          icon={<TextOnScreenIcon fill={iconColor ? iconColor : null} />}
          disabled={true}
          title={t("no_transcript_available_text")}
        />
      </Wrapper>
    );

  const handleOpenChange = (open: boolean) => {
    onOpenChange(open);
    if (open) {
      analytics.track({
        event: HallowAnalyticsEvent.TappedTextOnScreen,
        properties: {
          content_id: getContentId(currentItem),
          content_type: currentItem.type,
          has_transcript: !!currentItem.selected_audio.transcript_url,
          is_video: currentItem.selected_audio.media_type === "video",
          screen_name: "media_player",
        },
      });
    }
  };

  const isPlaying =
    currentItem.type === "prayer" &&
    currentItem.selected_audio.id === id &&
    !!player &&
    !player.paused;

  const currentTimeMs = (player?.currentTime ?? 0) * 1000;

  const isCurrentItem =
    currentItem.type === "prayer" && currentItem.selected_audio.id === id;

  const fontMultiplier = fontSizeToFontMultiplier(
    settingsQuery?.data?.global?.tos_font_size,
  );

  const theme =
    settingsQuery?.data?.global?.tos_bg_color === "content_color_hex"
      ? "color"
      : (renderedTheme as TranscriptTheme);

  const colorHex = currentItem.prayer.images.color_hex ?? colors.blackOLED;

  const isAutoScrollEnabled =
    settingsQuery?.data?.global?.tos_default_auto_scroll === "true";

  const isLoading = settingsQuery.isLoading || queue.loading;

  return (
    <FullscreenModal
      trigger={
        <Wrapper asChild>
          <IconButton
            ref={tosTrigger}
            variant={"transparentWhite"}
            icon={<TextOnScreenIcon fill={iconColor ? iconColor : null} />}
            onClick={onClick}
            title={t("text_on_screen_read_along")}
            tabIndex={0}
            css={
              currentItem.selected_audio.media_type === "video" &&
              hiddenTriggerStyle
            }
          />
        </Wrapper>
      }
      removeOverlay
      open={open}
      onOpenChange={handleOpenChange}
      modal={true}
      contentProps={{
        onInteractOutside: (e) => e.preventDefault(),
      }}
      closeCSS={[closeButtonStyle, theme === "color" && onColorStyle]}
      contentCSS={[
        modalStyle,
        theme === "color" ? colorModeStyle(colorHex) : null,
      ]}
      portalProps={{ container: popupRef.current }}
    >
      <Transcript
        content={{
          transcriptUrl: currentItem.selected_audio.transcript_url,
          transcriptType: currentItem.selected_audio.transcript_type,
          title: currentItem.prayer.title,
          imageUrl: currentItem.prayer.images.medium,
          isCurrentItem,
          isAutotranscribed: currentItem.selected_audio.is_autotranscribed,
        }}
        playback={{
          playing: isPlaying,
          currentTime: currentTimeMs,
          isAutoScrolling:
            currentItem.selected_audio.transcript_type === "vtt" &&
            isAutoScrollEnabled &&
            !isManuallyScrolling,
        }}
        settings={{
          theme,
          colorHex,
          fontMultiplier,
          isAutoScrollEnabled,
        }}
        callbacks={{
          onPlay: () => player?.playNew({ type: "audio", id }),
          onReport: handleReport,
          onUserScroll: isAutoScrollEnabled
            ? () => {
                setIsManuallyScrolling(true);
              }
            : undefined,
          onFirstViewed: () => {
            analytics.track({
              event: HallowAnalyticsEvent.ViewedTextOnScreen,
              properties: {
                prayer: prayerId,
                auto_scroll_enabled:
                  settingsQuery?.data?.global?.tos_default_auto_scroll,
                text_size: settingsQuery?.data?.global?.tos_font_size,
                color:
                  settingsQuery?.data?.global?.tos_bg_color ===
                  "content_color_hex"
                    ? "color"
                    : "system_theme",
              },
            });
          },
          excludeFromUserInteractionRef: followAudioButtonRef,
        }}
        isLoading={isLoading}
      >
        <TextSettings />
        {isManuallyScrolling && isAutoScrollEnabled && (
          <FollowAudioButton
            ref={followAudioButtonRef}
            onClick={() => setIsManuallyScrolling(false)}
          />
        )}
      </Transcript>
    </FullscreenModal>
  );
};
